import React from 'react'
import styled, { css } from 'styled-components'
import BrandLogo from 'uiComponents/atoms/BrandLogo/BrandLogo'
import { HeadingMS, HeadingXXXL } from 'uiComponents/atoms/Text/Headings'
import { complementaryColor } from 'uiComponents/constants/colors'
import { fwHeavy } from 'uiComponents/constants/typography'
import HeroTeaser from 'uiComponents/molecules/HeroTeaser/HeroTeaser'
import StoryTeaserBadges from 'uiComponents/molecules/StoryBadges/StoryBadges'

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  height: 100%;
`

const Title = styled(HeadingXXXL)<{ hasLogo: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  line-height: 1.02;
  letter-spacing: 0.6px;
  color: ${complementaryColor};
  padding: 0 25%;

  ${({ hasLogo }) =>
    hasLogo &&
    css`
      height: 60%;
    `};
`

const Subtitle = styled(HeadingMS.withComponent('div'))`
  ${fwHeavy};
  letter-spacing: 1.17px;
  text-align: center;
  left: 0;
  right: 0;
  margin: 0 auto 20px;
  color: ${complementaryColor};
  display: block;
`

const StyledHeroTeaser = styled(HeroTeaser)<{
  largeTeaser: boolean
}>`
  ${({ largeTeaser }) =>
    largeTeaser &&
    css`
      margin-top: 0;
    `};
`

interface IProps {
  src: string
  fullscreen?: boolean
  title: string
  subtitle?: string
  isSale?: boolean
  isSponsored?: boolean
  isPremium?: boolean
  largeTeaser: boolean
  targetScrollElementRef?: React.RefObject<HTMLElement | null>
  transparentLogoSrc?: string
  backButton: React.ReactNode
  genderSwitch?: React.ReactNode
  alt?: string
  showTitleAsDiv?: boolean
}

const PageTeaser: React.FC<IProps> = props => {
  const {
    src,
    fullscreen = false,
    title,
    subtitle,
    isSale = false,
    largeTeaser = false,
    isPremium = false,
    isSponsored = false,
    targetScrollElementRef,
    transparentLogoSrc,
    backButton,
    genderSwitch,
    alt,
    showTitleAsDiv = false,
  } = props

  const navigationButtons = [backButton]

  return (
    <StyledHeroTeaser
      targetScrollElementRef={targetScrollElementRef}
      fullscreen={fullscreen}
      src={src}
      largeTeaser={largeTeaser}
      navigationElements={navigationButtons}
      alt={alt}
      genderSwitch={genderSwitch}
    >
      <TitleContainer>
        <Title
          as={showTitleAsDiv ? 'div' : 'h1'}
          data-testid="StoryTitle"
          hasLogo={Boolean(transparentLogoSrc)}
        >
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {transparentLogoSrc ? (
            <BrandLogo
              src={transparentLogoSrc}
              alt={title}
              dataTestId="TransparentLogo"
            />
          ) : (
            title
          )}
        </Title>
        {!transparentLogoSrc && (
          <StoryTeaserBadges
            premium={isPremium}
            sale={isSale}
            sponsored={isSponsored}
          />
        )}
      </TitleContainer>
    </StyledHeroTeaser>
  )
}

export default PageTeaser
